import { gql, Variables } from 'graphql-request'
import { useEffect, useState, type Dispatch, type SetStateAction } from 'react'
import { useQuery, type UseQueryResult } from '@/proxy/react-query'
import { client } from '@/proxy/client'
import { OrderHistory, QueryDataError } from '@/resources/types'

export const ORDER_HISTORY = gql`
  query sellerOrderHistory(
    $pagination: PaginationInput!
    $period: PeriodInput!
    $search: OrderHistorySearchInput
    $filter: OrderHistoryFilterInput
  ) {
    orderHistory(
      pagination: $pagination
      period: $period
      search: $search
      filter: $filter
    ) {
      id
      number
      date
      customer {
        name
      }
      delivery {
        type
        deliveryDate {
          startDate
          endDate
        }
      }
      payment {
        type
      }
      charging {
        coupon {
          code
        }
        discount
        total
      }
      rating {
        value
      }
      status
      courierEmail
    }
  }
`

export const ORDER_HISTORY_QUERY_KEY = 'useOrderHistory'

export interface OrderHistoryData {
  orderHistory?: OrderHistory[]
  error?: QueryDataError
}

export const useOrderHistory = (): [
  Dispatch<SetStateAction<Variables | null>>,
  UseQueryResult<OrderHistoryData>,
] => {
  const [enabled, setEnabled] = useState(false)
  const [variables, setVariables] = useState<Variables | null>(null)

  useEffect(() => {
    if (variables !== null) setEnabled(true)
  }, [variables])

  const result = useQuery<OrderHistoryData>({
    queryKey: [ORDER_HISTORY_QUERY_KEY, variables, ORDER_HISTORY],
    queryFn: async ({ signal }) =>
      client.request({
        document: ORDER_HISTORY,
        signal,
        variables: variables as Variables,
      }),
    enabled,
    gcTime: 0,
  })

  useEffect(() => {
    if (!enabled || result.isFetching) return
    if (result.status === 'success' || result.status === 'error') {
      setEnabled(false)
    }
  }, [enabled, result.isFetching, result.status])

  return [setVariables, result]
}
